import { z } from 'zod';
import { isAllowedYoutubeUrl } from '../lib/youtube.js';

// ─── Ingestion Jobs ───────────────────────────────────────────────────────────

export const IngestionJobTypeSchema = z.enum(['file-upload', 'youtube']);
export type IngestionJobType = z.infer<typeof IngestionJobTypeSchema>;

export const IngestionJobStatusSchema = z.enum(['pending', 'processing', 'completed', 'failed']);
export type IngestionJobStatus = z.infer<typeof IngestionJobStatusSchema>;

export const IngestionJobSchema = z.object({
  id: z.string().uuid(),
  type: IngestionJobTypeSchema,
  status: IngestionJobStatusSchema,
  songId: z.string().uuid().optional(),
  progress: z.number().int().min(0).max(100),
  errorMessage: z.string().optional(),
  /** Original filename (for file-upload jobs) */
  filename: z.string().optional(),
  /** Source URL (for youtube jobs) */
  sourceUrl: z.string().url().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type IngestionJob = z.infer<typeof IngestionJobSchema>;

/** Body for submitting a YouTube ingest request */
export const YoutubeIngestSchema = z.object({
  url: z.string().url().refine(isAllowedYoutubeUrl, 'Must be a supported YouTube URL'),
  /** Optional metadata overrides (applied after yt-dlp extraction) */
  title: z.string().max(300).optional(),
  artist: z.string().max(200).optional(),
  tags: z.array(z.string().max(50)).max(30).optional(),
  notes: z.string().max(2000).optional(),
});
export type YoutubeIngestInput = z.infer<typeof YoutubeIngestSchema>;

/** SSE event payload for job progress updates */
export const JobProgressEventSchema = z.object({
  jobId: z.string().uuid(),
  status: IngestionJobStatusSchema,
  progress: z.number().int().min(0).max(100),
  songId: z.string().uuid().optional(),
  errorMessage: z.string().optional(),
});
export type JobProgressEvent = z.infer<typeof JobProgressEventSchema>;
